import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { KYC_LABEL, MODE_LABEL, instant, mad } from '../lib/format';
import { Badge, EmptyState, Notice, Select, Spinner, TextInput, Toggle } from '../components/ui';
import type { AdminOverview, PropertyDetail } from '../lib/types';
import AdminAffiliates from './admin/Affiliates';
import AdminDashboard from './admin/Dashboard';
import { t, translatedRecord } from '../lib/i18n';

/**
 * Admin: the two queues that gate the marketplace (listings awaiting a visit,
 * identity files awaiting a human), plus moderation and the maintenance jobs.
 *
 * Approving a listing is the only way it becomes ACTIVE — a host cannot do it —
 * and every action here writes an audit-log row.
 *
 * Laid out for a phone first: the three queues are tappable counters at the
 * top, every section is visible without scrolling sideways, and each decision
 * is one full-width button.
 */

type PendingKyc = {
  id: string;
  accountType: string;
  documentType: string;
  providerName: string;
  providerReferenceId: string;
  holderName: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  city: string | null;
  iceNumber: string | null;
  selfieVerified: boolean;
  createdAt: string;
  user: { id: string; fullName: string; phone: string; city: string | null; createdAt: string };
};

type Report = {
  id: string;
  targetType: string;
  targetId: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string;
  reporter: { id: string; fullName: string };
};

type Tab = 'dashboard' | 'listings' | 'kyc' | 'reports' | 'users' | 'affiliates' | 'jobs';

const ACCOUNT_LABEL: Record<string, string> = translatedRecord({
  INDIVIDUAL: 'Particulier',
  BUSINESS: 'Société / agence immobilière',
});

const DOCUMENT_LABEL: Record<string, string> = translatedRecord({
  CNIE: 'CNIE',
  PASSPORT: 'Passeport',
  RESIDENCE_PERMIT: 'Carte de séjour',
});

/*
 * The reasons are stored in French — they become the host's notification, which
 * is translated on display — so the host reads why, not a generic refusal.
 */
const LISTING_REJECT_REASONS = [
  'Photos ou informations insuffisantes',
  'Les photos contiennent du texte ou des numéros',
  'L’adresse ou le point sur la carte est incorrect',
  'Le prix ne correspond pas au logement',
  'Le logement ne correspond pas à la visite',
];

const KYC_REJECT_REASONS = [
  'Photo floue — le texte est illisible',
  'Le document a expiré',
  'Le nom ne correspond pas au compte',
  'Le selfie ne correspond pas au document',
];

export default function Admin() {
  const { toast, reportError } = useApp();
  // The dashboard first: it is what an admin opens the page to see; the
  // queues are one tap away, with their counts on the tabs.
  const [tab, setTab] = useState<Tab>('dashboard');
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [listings, setListings] = useState<PropertyDetail[] | null>(null);
  const [kyc, setKyc] = useState<PendingKyc[] | null>(null);
  const [reports, setReports] = useState<Report[] | null>(null);
  const [users, setUsers] = useState<
    { id: string; fullName: string; phone: string; roles: string; status: string; kycStatus: string }[]
  >([]);
  const [userQuery, setUserQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [visited, setVisited] = useState(true);
  const [jobResult, setJobResult] = useState<Record<string, number> | null>(null);
  // Which card has its refusal reasons open, and the reason picked there.
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    try {
      const [overviewResponse, listingResponse, kycResponse, reportResponse] = await Promise.all([
        api.get<AdminOverview>('/admin/overview'),
        api.get<{ properties: PropertyDetail[] }>('/admin/listings/pending'),
        api.get<{ submissions: PendingKyc[] }>('/admin/kyc/pending'),
        api.get<{ reports: Report[] }>('/admin/reports?status=OPEN'),
      ]);
      setOverview(overviewResponse);
      setListings(listingResponse.properties);
      setKyc(kycResponse.submissions);
      setReports(reportResponse.reports);
    } catch (error) {
      reportError(error);
    }
  }, [reportError]);

  useEffect(() => {
    void load();
  }, [load]);

  const searchUsers = async (event?: FormEvent) => {
    event?.preventDefault();
    try {
      const response = await api.get<{ users: typeof users }>(
        `/admin/users${userQuery ? `?q=${encodeURIComponent(userQuery)}` : ''}`,
      );
      setUsers(response.users);
    } catch (error) {
      reportError(error);
    }
  };

  const act = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      toast(message, 'ok');
      setRejecting(null);
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const open = (next: Tab) => {
    setTab(next);
    setRejecting(null);
    if (next === 'users' && users.length === 0) void searchUsers();
  };

  const startRejecting = (id: string, reasons: string[]) => {
    setRejecting(id);
    setRejectReason(reasons[0] ?? '');
  };

  if (!overview) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement de l’espace admin…")} />
      </div>
    );
  }

  const queues: { tab: Tab; label: string; count: number }[] = [
    { tab: 'listings', label: t('Annonces à vérifier'), count: overview.queues.pendingListings },
    { tab: 'kyc', label: t('Identités à valider'), count: overview.queues.pendingKyc },
    { tab: 'reports', label: t('Signalements ouverts'), count: overview.queues.openReports },
  ];

  const tabs: { value: Tab; label: string; count?: number }[] = [
    { value: 'dashboard', label: t('Tableau de bord') },
    { value: 'listings', label: t('Annonces'), count: overview.queues.pendingListings },
    { value: 'kyc', label: t('Identités'), count: overview.queues.pendingKyc },
    { value: 'reports', label: t('Signalements'), count: overview.queues.openReports },
    { value: 'users', label: t('Utilisateurs') },
    { value: 'affiliates', label: t('Affiliés') },
    { value: 'jobs', label: t('Maintenance') },
  ];

  const reasonPicker = (reasons: string[], onConfirm: () => void) => (
    <div className="card card--pad stack stack--tight admin-reject">
      <span className="small strong">{t('Motif du refus (envoyé à la personne)')}</span>
      <Select
        value={rejectReason}
        onChange={setRejectReason}
        options={reasons.map((reason) => ({ value: reason, label: t(reason) }))}
      />
      <div className="action-row">
        <button type="button" className="btn btn--danger" disabled={busy} onClick={onConfirm}>
          {t('Confirmer le refus')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => setRejecting(null)}>
          {t('Annuler')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="page stack">
      <h1 className="page-title">{t("Espace administrateur")}</h1>

      {/* The three queues: a count to act on, one tap from the list. */}
      <div className="queue-strip">
        {queues.map((queue) => (
          <button
            key={queue.tab}
            type="button"
            className={`queue-card ${queue.count > 0 ? 'queue-card--due' : ''} ${tab === queue.tab ? 'queue-card--on' : ''}`}
            onClick={() => open(queue.tab)}
          >
            <span className="queue-card__count">{queue.count}</span>
            <span className="queue-card__label">{queue.label}</span>
          </button>
        ))}
      </div>

      <nav className="tabs tabs--grid" aria-label={t('Sections')}>
        {tabs.map((entry) => (
          <button
            key={entry.value}
            type="button"
            className={`tab ${tab === entry.value ? 'tab--on' : ''}`}
            aria-current={tab === entry.value ? 'page' : undefined}
            onClick={() => open(entry.value)}
          >
            {entry.label}
            {entry.count ? <span className="tab__count">{entry.count}</span> : null}
          </button>
        ))}
      </nav>

      {tab === 'dashboard' ? <AdminDashboard /> : null}
      {tab === 'affiliates' ? <AdminAffiliates /> : null}

      {tab === 'listings' ? (
        listings === null ? (
          <Spinner />
        ) : listings.length === 0 ? (
          <EmptyState title={t("Aucune annonce en attente")} description={t("La file est vide.")} />
        ) : (
          <div className="stack">
            <div className="card card--pad">
              <Toggle
                on={visited}
                onChange={setVisited}
                label={t("La visite de vérification a été effectuée (active le badge « Visité »)")}
              />
            </div>
            {listings.map((property) => {
              const cover = property.photos.find((photo) => photo.isCover) ?? property.photos[0];
              return (
                <article key={property.id} className="card review-card">
                  <a className="review-card__media" href={`/logement/${property.id}`} target="_blank" rel="noreferrer">
                    {cover ? <img src={cover.url} alt="" loading="lazy" /> : <span className="small muted">{t('Aucune photo')}</span>}
                    <span className="badge badge--solid review-card__count">
                      {property.photos.length} {t("photo(s)")}
                    </span>
                  </a>
                  <div className="review-card__body stack stack--tight">
                    <span className="strong">{property.title}</span>
                    <span className="small muted">
                      {property.neighborhood}, {t(property.city)} · {property.rooms.length} {t("chambre(s)")}
                    </span>
                    <span className="small muted">
                      {t("Adresse :")} {property.addressPrecise ?? t("non renseignée")}
                    </span>
                    <span className="small">
                      {t("Hôte :")} {property.host?.fullName}{' '}
                      <Badge tone={property.host?.identityVerified ? 'badge--ok' : 'badge--warn'}>
                        {property.host?.identityVerified ? t('Identité vérifiée') : t('Identité non vérifiée')}
                      </Badge>
                    </span>
                    <div className="row row--wrap" style={{ gap: 6 }}>
                      {property.rentalModes
                        .filter((mode) => mode.isActive)
                        .map((mode) => (
                          <Badge key={mode.id}>
                            {MODE_LABEL[mode.mode] ?? mode.mode} · {mad(mode.priceCentimes)}
                          </Badge>
                        ))}
                    </div>
                  </div>
                  <div className="review-card__actions">
                    {rejecting === property.id ? (
                      reasonPicker(LISTING_REJECT_REASONS, () =>
                        act(
                          () =>
                            api.post(`/admin/listings/${property.id}/review`, {
                              decision: 'REJECT',
                              reason: rejectReason,
                            }),
                          t("Annonce renvoyée en brouillon."),
                        ),
                      )
                    ) : (
                      <div className="action-row">
                        <button
                          className="btn"
                          disabled={busy}
                          onClick={() =>
                            act(
                              () =>
                                api.post(`/admin/listings/${property.id}/review`, {
                                  decision: 'APPROVE',
                                  visited,
                                }),
                              t("Annonce approuvée et mise en ligne."),
                            )
                          }
                        >
                          {t("Approuver")}
                        </button>
                        <button
                          className="btn btn--danger"
                          disabled={busy}
                          onClick={() => startRejecting(property.id, LISTING_REJECT_REASONS)}
                        >
                          {t("Refuser")}
                        </button>
                        <a className="btn btn--ghost" href={`/logement/${property.id}`} target="_blank" rel="noreferrer">
                          {t("Voir")}
                        </a>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )
      ) : null}

      {tab === 'kyc' ? (
        kyc === null ? (
          <Spinner />
        ) : kyc.length === 0 ? (
          <EmptyState title={t("Aucune identité en attente")} description={t("La file est vide.")} />
        ) : (
          <div className="stack">
            <Notice tone="info">
              {t("Aucune image de document n’est stockée par Bedly. Ouvrez le dossier chez le prestataire avec la référence ci-dessous.")}
            </Notice>
            {kyc.map((submission) => (
              <article key={submission.id} className="card card--pad stack">
                <div className="row row--between row--wrap" style={{ gap: 8 }}>
                  <span className="strong">{submission.user.fullName}</span>
                  <Badge tone={submission.selfieVerified ? 'badge--ok' : 'badge--warn'}>
                    {submission.selfieVerified ? t("Selfie fourni") : t("Selfie manquant")}
                  </Badge>
                </div>
                <dl className="facts">
                  <dt>{t('Compte')}</dt>
                  <dd>{ACCOUNT_LABEL[submission.accountType] ?? submission.accountType}</dd>
                  <dt>{t('Document')}</dt>
                  <dd>{DOCUMENT_LABEL[submission.documentType] ?? submission.documentType}</dd>
                  {submission.iceNumber ? (
                    <>
                      <dt>ICE</dt>
                      <dd>{submission.iceNumber}</dd>
                    </>
                  ) : null}
                  <dt>{t('Titulaire')}</dt>
                  <dd>{submission.holderName ?? '—'}</dd>
                  <dt>{t('Naissance')}</dt>
                  <dd>{submission.dateOfBirth ?? t('date inconnue')}</dd>
                  <dt>{t('Nationalité')}</dt>
                  <dd>{submission.nationality ?? '—'}</dd>
                  <dt>{t('Ville')}</dt>
                  <dd>{submission.city ? t(submission.city) : '—'}</dd>
                  <dt>{t('Référence')}</dt>
                  <dd className="facts__code">
                    {submission.providerName} : {submission.providerReferenceId}
                  </dd>
                  <dt>{t('Envoyé')}</dt>
                  <dd>{instant(submission.createdAt)}</dd>
                </dl>
                {rejecting === submission.id ? (
                  reasonPicker(KYC_REJECT_REASONS, () =>
                    act(
                      () =>
                        api.post(`/admin/kyc/${submission.id}/review`, {
                          decision: 'REJECTED',
                          rejectionReason: rejectReason,
                        }),
                      t("Identité refusée."),
                    ),
                  )
                ) : (
                  <div className="action-row">
                    <button
                      className="btn"
                      disabled={busy}
                      onClick={() =>
                        act(
                          () =>
                            api.post(`/admin/kyc/${submission.id}/review`, {
                              decision: 'APPROVED',
                              documentLast4: '0000',
                            }),
                          t("Identité approuvée."),
                        )
                      }
                    >
                      {t("Approuver")}
                    </button>
                    <button
                      className="btn btn--danger"
                      disabled={busy}
                      onClick={() => startRejecting(submission.id, KYC_REJECT_REASONS)}
                    >
                      {t("Refuser")}
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )
      ) : null}

      {tab === 'reports' ? (
        reports === null ? (
          <Spinner />
        ) : reports.length === 0 ? (
          <EmptyState title={t("Aucun signalement ouvert")} />
        ) : (
          <div className="stack">
            {reports.map((report) => (
              <article key={report.id} className="card card--pad stack stack--tight">
                <div className="row row--between row--wrap" style={{ gap: 8 }}>
                  <span className="strong">{report.reason}</span>
                  <span className="tiny muted">{instant(report.createdAt)}</span>
                </div>
                {report.details ? <p className="small" style={{ margin: 0 }}>{report.details}</p> : null}
                <span className="tiny muted">
                  {t("Signalé par")} {report.reporter.fullName} {t("· cible")} {report.targetType}{' '}
                  <span className="facts__code">{report.targetId}</span>
                </span>
                <div className="action-row">
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() =>
                      act(
                        () => api.post(`/admin/reports/${report.id}/resolve`, { status: 'RESOLVED' }),
                        t("Signalement résolu."),
                      )
                    }
                  >
                    {t("Résoudre")}
                  </button>
                  <button
                    className="btn btn--ghost"
                    disabled={busy}
                    onClick={() =>
                      act(
                        () => api.post(`/admin/reports/${report.id}/resolve`, { status: 'DISMISSED' }),
                        t("Signalement rejeté."),
                      )
                    }
                  >
                    {t("Rejeter")}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )
      ) : null}

      {tab === 'users' ? (
        <div className="card card--pad stack">
          <form className="search-row" onSubmit={searchUsers}>
            <TextInput
              value={userQuery}
              onChange={setUserQuery}
              placeholder={t("Nom, téléphone ou email")}
              type="search"
              enterKeyHint="search"
            />
            <button type="submit" className="btn btn--ghost">
              {t("Rechercher")}
            </button>
          </form>
          {users.length === 0 ? (
            <p className="small muted" style={{ margin: 0 }}>{t('Aucun résultat.')}</p>
          ) : (
            <table className="table table--stack">
              <thead>
                <tr>
                  <th>{t("Nom")}</th>
                  <th>{t("Téléphone")}</th>
                  <th>{t("Rôles")}</th>
                  <th>{t('Identité')}</th>
                  <th>{t("Statut")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="strong" data-label={t("Nom")}>{user.fullName}</td>
                    <td className="tiny" data-label={t("Téléphone")}>{user.phone || '—'}</td>
                    <td className="tiny" data-label={t("Rôles")}>{user.roles}</td>
                    <td className="tiny" data-label={t('Identité')}>{KYC_LABEL[user.kycStatus] ?? user.kycStatus}</td>
                    <td data-label={t("Statut")}>
                      <Badge tone={user.status === 'ACTIVE' ? 'badge--ok' : 'badge--danger'}>
                        {user.status === 'ACTIVE' ? t('Actif') : t('Suspendu')}
                      </Badge>
                    </td>
                    <td className="table__action">
                      <button
                        className={`btn btn--sm ${user.status === 'ACTIVE' ? 'btn--danger' : 'btn--ghost'}`}
                        disabled={busy}
                        onClick={() => {
                          if (
                            user.status === 'ACTIVE' &&
                            !window.confirm(t('Suspendre {name} ?', { name: user.fullName }))
                          ) {
                            return;
                          }
                          void act(async () => {
                            await api.post(`/admin/users/${user.id}/suspend`, {
                              suspended: user.status === 'ACTIVE',
                              reason: 'Décision de modération',
                            });
                            await searchUsers();
                          }, user.status === 'ACTIVE' ? t("Compte suspendu.") : t("Compte réactivé."));
                        }}
                      >
                        {user.status === 'ACTIVE' ? t("Suspendre") : t("Réactiver")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {tab === 'jobs' ? (
        <div className="card card--pad stack">
          <h2>{t("Tâches de maintenance")}</h2>
          <p className="small muted" style={{ margin: 0 }}>
            {t("Expirer les demandes sans réponse, clôturer les séjours terminés, publier les avis dus après 14 jours, expirer les demandes de colocation dépassées. Le planificateur fait la même chose toutes les 5 minutes ; ce bouton la déclenche maintenant.")}
          </p>
          <button
            className="btn btn--block-mobile"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const response = await api.post<Record<string, number>>('/admin/jobs/run');
                setJobResult(response);
                toast(t("Tâches exécutées."), 'ok');
                await load();
              } catch (error) {
                reportError(error);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? t("Exécution…") : t("Exécuter maintenant")}
          </button>
          {jobResult ? (
            <div className="card card--pad stack stack--tight">
              {Object.entries(jobResult).map(([key, value]) => (
                <div key={key} className="row row--between small">
                  <span className="muted">{key}</span>
                  <span className="strong">{value}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
